import React, { FC, useMemo } from 'react';
import { useToggle } from 'react-use';
import { useAddress, useContract, useNFT } from '@thirdweb-dev/react';
import { NFTMarketplaceListing } from '@app/types/nftMarketplace';
import {
  AspectRatio,
  Box,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Text,
  VStack
} from '@chakra-ui/react';
import { SellPrice } from '@app/screens/nft-marketplace/SellPrice';
import { RemoveItemButton } from '@app/screens/nft-marketplace/Cart/RemoveItemButton';
import { RxHamburgerMenu } from 'react-icons/rx';
import { NftCartItemAnimatedContainer } from '@app/screens/nft-marketplace/Cart/NftCartItemAnimatedContainer';
import { DetailsSection } from '@app/screens/nft-marketplace/MakeOfferDialog/components/DetailsSection';
import { getAuctionDescription } from '@app/helpers/nft/getAuctionDescription';
import { formatUnits } from 'ethers/lib/utils';
import { formatNumber } from '@app/helpers/format';
import { useTokenBalance } from '@app/hooks/token/useTokenBalance';
import { useGetBestBidForListing } from '@app/hooks/nft/useGetBestBidForListing';
import { AnimatePresence } from 'framer-motion';
import { useFindTokenInWhitelist } from '@app/hooks/nft/useFindTokenInWhitelist';

type Props = {
  isMobile: boolean;
  listing: NFTMarketplaceListing;
};

export const NftRewardCartItem: FC<Props> = ({ listing, isMobile }) => {
  const [openDetails, toggleOpen] = useToggle(false);
  const address = useAddress();
  const { id, tokenId, isNFTAvailable, nftMetadata } = listing;
  const { contract } = useContract(listing.nftContractAddress);
  const { data: nft, isLoading } = useNFT(contract, tokenId);

  const token = useFindTokenInWhitelist(listing);

  const { data: bestBidForListing } = useGetBestBidForListing(listing.id);

  const { balance } = useTokenBalance(token!.contractAddress, address ?? '');

  if (!token || !nft || !bestBidForListing) {
    return null;
  }

  const renderRewardTrait = () => {
    if (nft?.metadata && Array.isArray(nft?.metadata.attributes)) {
      const trait = nft?.metadata.attributes[0] as {
        trait_type: string;
        value: string;
      };

      return (
        <HStack
          justifyContent="space-between"
          padding={isMobile ? '0' : '2px 8px'}
        >
          <Text
            fontSize={isMobile ? '12px' : '14px'}
            lineHeight="26px"
            fontWeight="400"
            color="meme"
          >
            {trait.trait_type} - {trait.value}
          </Text>
        </HStack>
      );
    }

    return null;
  };

  const renderDetails = () => {
    return (
      <NftCartItemAnimatedContainer>
        <GridItem width="100%" padding="16px">
          <DetailsSection
            auctionType={listing.type}
            auctionDescription={getAuctionDescription(listing.type)}
            bestOffer={
              bestBidForListing?.amount && token
                ? `${formatUnits(bestBidForListing?.amount, token?.decimals)} ${
                    token.symbol
                  }`
                : '-'
            }
            balance={
              balance ? `${formatNumber(+balance, 3)} ${token!.symbol}` : '-'
            }
            reservePrice={
              listing.reserve && token
                ? `${formatUnits(listing.reserve, token?.decimals)} ${
                    token.symbol
                  }`
                : '-'
            }
          />
          {isMobile ? (
            <HStack width="100%" justifyContent="space-between">
              <RemoveItemButton id={listing.id} />
            </HStack>
          ) : null}
        </GridItem>
      </NftCartItemAnimatedContainer>
    );
  };

  return (
    <>
      {isMobile ? (
        <Grid
          width="100%"
          borderRadius="8px"
          border="1px solid"
          borderColor={isNFTAvailable ? 'gray.15' : 'error'}
          backgroundColor="black"
          justifyItems="center"
          padding="8px 16px"
          templateColumns="repeat(auto-fit, minmax(50px, 1fr))"
          alignItems="center"
          gap={1}
        >
          <GridItem>
            <HStack>
              <AspectRatio
                ratio={1}
                boxSize="50px"
                pos="relative"
                overflow="hidden"
                maxHeight={50}
                minWidth={50}
                borderRadius="8px"
              >
                {nft && nft.metadata.image && nft.metadata.name ? (
                  <img
                    src={nft.metadata.image}
                    alt={nft.metadata.name.toString() ?? ''}
                    style={{
                      objectFit: 'cover',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                ) : (
                  <Box
                    height="30px"
                    width="30px"
                    backgroundColor="orange"
                    borderRadius="19px"
                  />
                )}
              </AspectRatio>
            </HStack>
          </GridItem>
          <GridItem>
            <Text
              fontSize="12px"
              lineHeight="12px"
              fontWeight="400"
              color="white"
            >
              {nft?.metadata.name}
            </Text>
            <Text
              fontSize="12px"
              lineHeight="26px"
              fontWeight="400"
              color="gray.15"
            >
              ID: {listing.tokenId}
            </Text>
          </GridItem>
          <GridItem>{renderRewardTrait()}</GridItem>
          <GridItem justifySelf="flex-start">
            {isNFTAvailable && token ? (
              <SellPrice listing={listing} currency={token} />
            ) : (
              <Text
                color="error"
                fontWeight="400"
                fontSize="16px"
                lineHeight="26px"
              >
                Expired
              </Text>
            )}
          </GridItem>
          <GridItem justifySelf="end">
            <IconButton
              onClick={() => toggleOpen()}
              aria-label="expand"
              icon={<RxHamburgerMenu />}
            />
          </GridItem>
          <AnimatePresence>
            {openDetails ? renderDetails() : null}
          </AnimatePresence>
        </Grid>
      ) : (
        <Grid
          width="100%"
          borderRadius="8px"
          border="1px solid"
          borderColor={isNFTAvailable ? 'gray.15' : 'error'}
          backgroundColor="black"
          justifyItems="center"
          padding="8px 16px"
          templateColumns="repeat(5, 1fr) 1.5fr .5fr 1fr"
          alignItems="center"
          gap={1}
        >
          <GridItem>
            <HStack>
              <AspectRatio
                ratio={1}
                boxSize="50px"
                pos="relative"
                overflow="hidden"
                maxHeight={50}
                minWidth={50}
                borderRadius="8px"
              >
                {nft && nft.metadata.image && nft.metadata.name ? (
                  <img
                    src={nft.metadata.image}
                    alt={nft.metadata.name.toString() ?? ''}
                    style={{
                      objectFit: 'cover',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                ) : (
                  <Box
                    height="30px"
                    width="30px"
                    backgroundColor="orange"
                    borderRadius="19px"
                  />
                )}
              </AspectRatio>
            </HStack>
          </GridItem>
          <GridItem>
            <Text
              fontSize="16px"
              lineHeight="26px"
              fontWeight="400"
              color="white"
            >
              {nft?.metadata.name}
            </Text>
            <Text
              fontSize="14px"
              lineHeight="26px"
              fontWeight="400"
              color="gray.15"
            >
              ID: {listing.tokenId}
            </Text>
          </GridItem>
          <GridItem colSpan={1} />
          <GridItem>{renderRewardTrait()}</GridItem>
          <GridItem colSpan={1} />
          <GridItem justifySelf="flex-start">
            {isNFTAvailable && token ? (
              <SellPrice listing={listing} currency={token} />
            ) : (
              <Text
                color="error"
                fontWeight="400"
                fontSize="16px"
                lineHeight="26px"
              >
                Expired
              </Text>
            )}
          </GridItem>
          <GridItem>
            <IconButton
              onClick={() => toggleOpen()}
              aria-label="expand"
              icon={<RxHamburgerMenu />}
            />
          </GridItem>
          <GridItem>
            <RemoveItemButton id={id} />
          </GridItem>
          <AnimatePresence>
            {openDetails ? renderDetails() : null}
          </AnimatePresence>
        </Grid>
      )}
    </>
  );
};
